import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

// HiPo Brand Guidelines Button System
// Royal Blue: #4C62E3, Coral: #EE7D79, Alto Grey: #D5D5D5
// Typography: Montserrat Bold for primary, Regular for secondary

function cn(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl font-display text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C62E3] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary: Royal Blue background, White text, Bold font
        primary:
          "bg-[#4C62E3] text-white font-bold shadow-sm hover:bg-[#3B4CC5] hover:text-white active:bg-[#2A3BA8] active:text-white",
        // Action/Highlight: Coral background, White text, Bold font  
        action:
          "bg-[#EE7D79] text-white font-bold shadow-sm hover:bg-[#E56B66] hover:text-white active:bg-[#DC5953] active:text-white",
        // Secondary: Light background, Royal Blue text, Regular font
        secondary:
          "bg-[#EEF1FF] text-[#4C62E3] font-normal hover:bg-[#DDE3FF] active:bg-[#CCD5FF]",
        // Outline: Border with Royal Blue, Regular font
        outline:
          "border border-[#D5D5D5] bg-white text-slate-600 font-normal hover:text-[#EE7D79] hover:border-[#EE7D79] active:bg-slate-50",
        // Ghost: No background, hover effect
        ghost: "text-slate-600 font-normal hover:bg-slate-100 active:bg-slate-200",
        // Destructive: Red tones for dangerous actions
        destructive:
          "bg-red-600 text-white font-bold shadow-sm hover:bg-red-700 active:bg-red-800",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        default: "h-10 px-4 py-2",
        lg: "h-12 px-6 py-3 text-base",
        xl: "h-14 px-8 py-4 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  icon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, icon, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {!loading && icon && <span className="mr-1">{icon}</span>}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

// Utility classes for direct Tailwind usage
export const buttonClasses = {
  primary: "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#4C62E3] px-4 py-2 font-display text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-[#3B4CC5] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C62E3] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:text-white",
  action: "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#EE7D79] px-4 py-2 font-display text-sm font-bold text-white shadow-sm transition-all duration-200 hover:bg-[#E56B66] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EE7D79] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:text-white",
  secondary: "inline-flex items-center justify-center gap-2 rounded-2xl bg-[#EEF1FF] px-4 py-2 font-display text-sm font-normal text-[#4C62E3] transition-all duration-200 hover:bg-[#DDE3FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C62E3] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  outline: "inline-flex items-center justify-center gap-2 rounded-2xl border border-[#D5D5D5] bg-white px-4 py-2 font-display text-sm font-normal text-slate-600 transition-all duration-200 hover:text-[#EE7D79] hover:border-[#EE7D79] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C62E3] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
};

export { Button, buttonVariants }
